import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link2, Copy, Check } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";
import { useToast } from "@/hooks/use-toast";

const UTMBuilder = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [medium, setMedium] = useState<string>("");
  const [campaign, setCampaign] = useState<string>("");
  const [term, setTerm] = useState<string>("");
  const [content, setContent] = useState<string>("");

  const generatedUrl = useMemo(() => {
    if (!baseUrl) return "";
    
    const params = new URLSearchParams();
    if (source) params.append("utm_source", source);
    if (medium) params.append("utm_medium", medium);
    if (campaign) params.append("utm_campaign", campaign);
    if (term) params.append("utm_term", term);
    if (content) params.append("utm_content", content);
    
    const paramString = params.toString();
    if (!paramString) return baseUrl;
    
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${paramString}`;
  }, [baseUrl, source, medium, campaign, term, content]);

  const copyToClipboard = async () => {
    if (!generatedUrl) return;
    
    await navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    toast({ title: "Copied to clipboard!" });
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    setBaseUrl("");
    setSource("");
    setMedium("");
    setCampaign("");
    setTerm("");
    setContent("");
  };

  const relatedTools = getRelatedTools("utm-builder");

  return (
    <ToolPageLayout
      title="UTM Builder"
      description="Create UTM-tagged URLs to track your marketing campaigns in Google Analytics and understand which channels drive results."
      metaDescription="UTM parameters are tags added to URLs that help you track the effectiveness of marketing campaigns in analytics tools. Use this builder to create properly formatted tracking URLs."
      icon={Link2}
      slug="utm-builder"
      formula={{
        equation: "URL + ?utm_source=X&utm_medium=Y&utm_campaign=Z",
        variables: [
          { name: "utm_source", description: "The referrer (google, facebook, newsletter)" },
          { name: "utm_medium", description: "Marketing medium (cpc, email, social)" },
          { name: "utm_campaign", description: "Campaign name (spring_sale, product_launch)" },
          { name: "utm_term", description: "Paid search keywords (optional)" },
          { name: "utm_content", description: "Content differentiation (header_cta, sidebar)" },
        ]
      }}
      example={{
        inputs: [
          { label: "URL", value: "https://example.com/sale" },
          { label: "Source", value: "google" },
          { label: "Medium", value: "cpc" },
          { label: "Campaign", value: "summer_sale" },
        ],
        output: "https://example.com/sale?utm_source=google&utm_medium=cpc&utm_campaign=summer_sale",
        interpretation: "This URL will attribute any conversions to your Google CPC summer sale campaign in analytics."
      }}
      improvements={[
        "Use consistent naming conventions across all campaigns",
        "Document your UTM taxonomy in a shared spreadsheet",
        "Keep campaign names lowercase and use underscores",
        "Be specific with source and medium parameters",
        "Use utm_content to differentiate A/B test variants",
        "Review UTM data regularly to optimize channel mix",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Analytics & Attribution", href: "/analytics" }}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Build Your URL</CardTitle>
            <CardDescription>Fill in the parameters below</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="baseUrl">Website URL *</Label>
              <Input
                id="baseUrl"
                type="url"
                placeholder="https://example.com/landing-page"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="source">Campaign Source (utm_source) *</Label>
              <Input
                id="source"
                placeholder="google, facebook, newsletter"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="medium">Campaign Medium (utm_medium) *</Label>
              <Input
                id="medium"
                placeholder="cpc, email, social"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="campaign">Campaign Name (utm_campaign) *</Label>
              <Input
                id="campaign"
                placeholder="spring_sale, product_launch"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="term">Campaign Term (utm_term)</Label>
              <Input
                id="term"
                placeholder="running+shoes (optional)"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Campaign Content (utm_content)</Label>
              <Input
                id="content"
                placeholder="header_banner, sidebar_cta"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            <Button variant="outline" onClick={reset} className="w-full">
              Reset All
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Generated URL</CardTitle>
            <CardDescription>Your campaign URL with UTM parameters</CardDescription>
          </CardHeader>
          <CardContent>
            {generatedUrl ? (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg break-all text-sm font-mono">
                  {generatedUrl}
                </div>
                <Button onClick={copyToClipboard} className="w-full">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy URL
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">
                Enter a URL and parameters to generate your tracking link.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </ToolPageLayout>
  );
};

export default UTMBuilder;
